'use client';

import React, { useState, useEffect, useRef, JSX } from 'react';

import { Text, Slider } from '@videoblocks/design-system';
import ChevronDown from '@videoblocks/react-icons/ChevronDown';

const Dropdown = ({
    value,
    minValue,
    maxValue,
    onChange,
    header,
}: {
    value: number;
    minValue: number;
    maxValue: number;
    onChange: (value: number) => void;
    header: string;
}): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const formattedValue = `${value / 100}x`;
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Main Button */}
            <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="bg-gray-800 px-4 py-4 rounded-md flex items-center gap-2 justify-between cursor-pointer min-w-[124px] hover:bg-gray-700"
            >
                <Text fontSize="sm" fontWeight="regular">
                    {formattedValue}
                </Text>
                <ChevronDown
                    className={`fill-current w-3 h-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 rounded-lg shadow-xl z-10 border border-gray-700 w-[285px]">
                    <div className="px-4 py-3 border-b border-gray-700">
                        <h3 className="text-white font-semibold text-lg">{header}</h3>
                    </div>

                    <div className="py-3 px-4 mt-2 flex flex-col gap-2 items-end">
                        <Slider
                            value={[value]}
                            min={minValue}
                            max={maxValue}
                            onValueChange={(newValue) => {
                                onChange(newValue[0]);
                            }}
                            trackProps={{
                                className: 'bg-white',
                            }}
                            rangeProps={{
                                className: 'bg-blue-600',
                            }}
                        />
                        <Text fontSize="sm" fontWeight="regular">
                            {formattedValue}
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dropdown;
